/**
 * Pending anonymous scans.
 *
 * A signed-out visitor's lookup is stored server-side by the Worker under a
 * one-time claim token (`_anon_claim_token` on the verification response). The
 * browser keeps only that token plus enough labelling to render the prompt.
 * Once the visitor signs in, `claimPendingScans` hands each token to the
 * `claim_anonymous_scan` RPC, which copies the server-verified result into their
 * history without consuming any of their free scans.
 */

import { supabase } from '../utils/supabase';

const STORAGE_KEY = 'checkcoa.pendingScans';

/** Matches the anonymous device allowance (2, lifetime) and the RPC's per-account cap. */
const MAX_PENDING = 2;

/** Matches the 7-day window enforced by claim_anonymous_scan. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingScan {
    token: string;
    company: string;
    certificateNumber: string;
    title?: string;
    landingPath?: string;
    savedAt: number;
}

export interface ClaimOutcome {
    claimed: number;
    /** Certificate numbers that landed in the user's history. */
    certificates: string[];
}

const isFresh = (entry: PendingScan): boolean => Date.now() - entry.savedAt < TTL_MS;

const isValid = (entry: unknown): entry is PendingScan =>
    typeof entry === 'object' &&
    entry !== null &&
    typeof (entry as PendingScan).token === 'string' &&
    typeof (entry as PendingScan).savedAt === 'number';

function read(): PendingScan[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isValid).filter(isFresh);
    } catch {
        // Corrupted or unavailable storage must never break the scanner.
        return [];
    }
}

function write(entries: PendingScan[]): void {
    if (typeof window === 'undefined') return;
    try {
        if (entries.length === 0) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Private mode / quota exceeded — the prompt simply won't persist.
    }
}

export function getPendingScans(): PendingScan[] {
    return read();
}

export function hasPendingScans(): boolean {
    return read().length > 0;
}

export function clearPendingScans(): void {
    write([]);
}

/**
 * Remember an anonymous result. Re-scanning the same token is a no-op; the
 * newest entries win once the visitor exceeds the anonymous allowance.
 */
export function rememberPendingScan(
    token: string,
    meta: Omit<PendingScan, 'token' | 'savedAt'>
): void {
    if (!token) return;
    const existing = read().filter(entry => entry.token !== token);
    const next = [...existing, { ...meta, token, savedAt: Date.now() }].slice(-MAX_PENDING);
    write(next);
}

/** Errors that mean the token will never succeed — drop it instead of retrying. */
const TERMINAL_ERRORS = new Set(['token_invalid_or_claimed', 'claim_limit_reached', 'invalid_args']);

/**
 * Claim every pending scan for the signed-in caller. Safe to call repeatedly:
 * the RPC is single-use per token and identifies the user from `auth.uid()`.
 */
export async function claimPendingScans(): Promise<ClaimOutcome> {
    const pending = read();
    if (pending.length === 0) return { claimed: 0, certificates: [] };

    const remaining: PendingScan[] = [];
    const certificates: string[] = [];

    for (const entry of pending) {
        try {
            const { data, error } = await supabase.rpc('claim_anonymous_scan', {
                p_claim_token: entry.token,
            });

            if (error) {
                // Transient (network/permission) — keep it for the next attempt.
                console.warn('[anonymousScan] claim failed, will retry:', error.message);
                remaining.push(entry);
                continue;
            }

            const result = data as { success?: boolean; error?: string; certificate_number?: string } | null;

            if (result?.success) {
                certificates.push(result.certificate_number || entry.certificateNumber);
                continue;
            }

            if (result?.error && !TERMINAL_ERRORS.has(result.error)) {
                remaining.push(entry);
            }
            // Terminal errors fall through: the entry is dropped.
        } catch (err) {
            console.warn('[anonymousScan] claim threw, will retry:', err);
            remaining.push(entry);
        }
    }

    write(remaining);
    return { claimed: certificates.length, certificates };
}
